<template>
    <div class="error background" :aria-label="`Error ${statusCode}, ${title}, ${message}`">
        <div class="container">
            <div class="inner">
                <div class="ds-box ds-box--shadow">
                    <h1 class="ds-title text-center status-code" :aria-label="`Error ${statusCode}`">
                        {{ statusCode }}
                    </h1>

                    <div class="body text-center">
                        <h3 class="ds-title text-center title">
                            {{ title }}
                        </h3>
                        <p>{{ message }}</p>
                        <div v-if="stack.length > 0" class="stack-trace">
                            <button class="stack" @click="stackOpen = !stackOpen">{{ $i18n.t('error.details') }}</button>
                            <DSHeightTransition :open="stackOpen"><div class="content" v-html="stack" /></DSHeightTransition>
                        </div>
                    </div>
                    <div class="links">
                        <NuxtLink
                            v-if="actions && actions.back"
                            class="ds-link ds-link--cta"
                            href="#"
                            @click="goBack"
                        >
                            {{ actions.back.buttonText }}
                        </NuxtLink>
                        <span class="ds-separator ds-separator--default" />
                        <NuxtLink
                            v-if="actions && actions.homepage"
                            class="ds-link ds-link--cta"
                            to="/"
                        >
                            {{ actions.homepage.buttonText }}
                        </NuxtLink>
                        <span v-if="actions && actions.reportProblem" class="ds-separator ds-separator--default" />
                        <NuxtLink
                            v-if="actions && actions.reportProblem"
                            class="ds-link ds-link--cta"
                            :href="actions.reportProblem.buttonLink"
                        >
                            {{ actions.reportProblem.buttonText }}
                        </NuxtLink>
                    </div>
                </div>
                <div v-if="usefulLinks.length" class="links-container">
                    <h4 class="title">
                        {{ usefulLinksTitle }}
                    </h4>
                    <DSUsefulLinks :links="usefulLinks" />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref } from 'vue';
import { useNuxtApp, useI18n } from '#imports';
const { $i18n } = useNuxtApp();
const stackOpen = ref(process.env.NODE_ENV === 'development');
defineProps({
    statusCode: {
        type: [ Number, String ],
        required: false,
        default: 'Oops!',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    message: {
        type: String,
        required: false,
        default: '',
    },
    actions: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    stack: {
        type: String,
        required: false,
        default: '',
    },
    usefulLinks: {
        type: Array,
        required: false,
        default() {
            return [];
        },
    },
    usefulLinksTitle: {
        type: String,
        required: false,
        default: '',
    },
});

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            error: {
                details: 'Lisätiedot',
            },
        },
        'en-EU': {
            error: {
                details: 'Details',
            },
        },
        'sv-SE': {
            error: {
                details: 'Tilläggsuppgifter',
            },
        },
    },
});
</script>
<script>
export default {
    name: 'DSErrorPage',
    methods: {
        goBack() {
            this.$router.back();
        },
    },
};
</script>

<style scoped>
.error {
  padding-top: 4rem;
  padding-bottom: 2rem;
}
.error.background {
  background-color: var(--color-base-grey-100);
}

.container {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.container .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}

.ds-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  margin-inline: 0;
  margin-bottom: 4rem;
}

.status-code {
  margin-top: 4rem;
  margin-bottom: 4rem;
  color: var(--color-base-black);
  font-size: 6rem;
  line-height: 1;
}
@media (min-width: 768px) {
  .status-code {
    font-size: 8rem;
  }
}

.body {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 4rem;
}
.body .title {
  margin-bottom: 0.5rem;
}
@media (max-width: 767px) {
  .body .title {
    font-size: 1.125rem;
  }
}
@media (max-width: 767px) {
  .body {
    font-size: 0.875rem;
  }
}

.stack-trace {
  overflow: hidden;
  max-width: 100%;
}
.stack-trace .stack {
  color: var(--color-base-grey-600);
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.stack-trace :deep(pre) {
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  border-radius: 0.25rem;
  text-align: left;
  white-space: pre-wrap;
  word-wrap: break-word;
}

h4 {
  font-size: 1.5rem;
}

.links {
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 767px) {
  .links {
    flex-direction: column;
  }
  .links :deep(.ds-separator) {
    display: none;
  }
}

.ds-link {
  padding: 0.5rem;
}
@media (max-width: 767px) {
  .ds-link {
    margin-bottom: 0.5rem;
  }
}

.links-container .title {
  margin-bottom: 1rem;
}
@media (max-width: 767px) {
  .links-container .title {
    font-size: 1.125rem;
  }
}

:deep(.subtitle) {
  font-size: 0.875rem;
}
</style>
